<template>
	<div class="portal-profile">
		<Text v-if="name" class="portal-profile__line" :font="fonts.name" :color="colors.name">{{ name }}</Text>
		<Text v-if="email" class="portal-profile__line" :font="fonts.email" :color="colors.email">{{ email }}</Text>
	</div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Text } from '@simple-life-app/growth-ui-kova'
import { useAuthStore } from '@/store/auth'

/**
 * The signed-in customer: full name and email (Figma 13833:135694 in the header, 14254:175882 in the side
 * menu). Name and email only — the v1 scope has no profile picture (GT-5504 removed it; KOVA-796 "Source
 * clarification"). A missing name leaves just the email; long values are cut with an ellipsis — and NOT kept
 * in a `title` tooltip: Sentry's `ui.click` breadcrumb records the clicked element's `title` / `aria-label` /
 * `alt`, which would ship the customer's name and email with every later event (sentry.md: no PII).
 */

const props = defineProps<{
	/** `inverse` on the dark header, `default` on the light side menu. */
	tone: 'inverse' | 'default'
}>()

const auth = useAuthStore()
const name = computed(() => auth.displayName)
const email = computed(() => auth.displayEmail)
const colors = computed(() =>
	props.tone === 'inverse'
		? { name: '--text-white-primary', email: '--text-white-secondary' }
		: { name: '--text-dark-primary', email: '--text-dark-secondary' },
)
// The header's lines are 19 tall, SF Pro's normal height in Figma (13833:135694: 19 + 2 + 19, the 40px a profile
// picture would take), so the bar stays 104 tall; no DS token is 19. The side menu keeps the DS 22px lines.
const fonts = computed(() =>
	props.tone === 'inverse'
		? {
				name: 'normal var(--fw-medi) var(--16) / 19px var(--font-default)',
				email: 'normal var(--fw-reg) var(--16) / 19px var(--font-default)',
			}
		: { name: '--text-16-22-med', email: '--text-16-22-reg' },
)
</script>

<style scoped>
.portal-profile {
	display: flex;
	flex-direction: column;
	gap: var(--size-2);
	min-width: 0;
	max-width: 100%;
}

.portal-profile__line {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}
</style>
